import React, { useState, useEffect } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import apiService from '../services/apiService';
import {
  ContentCard,
  CardTitle,
  FormGroup,
  ImageUpload,
  ImagePreview,
  ActionButtons,
  SaveButton,
  CancelButton,
  SuccessMessage,
  ErrorMessage
} from '../styles/AdminStyles';

const TeamMemberEditor = ({ teamMember, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    imageUrl: '',
    bio: '',
    email: '',
    linkedin: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (teamMember) {
      setFormData({
        name: teamMember.name || '',
        position: teamMember.position || '',
        imageUrl: teamMember.imageUrl || '',
        bio: teamMember.bio || '',
        email: teamMember.email || '',
        linkedin: teamMember.linkedin || ''
      });
      setImagePreview(teamMember.imageUrl || '');
    }
  }, [teamMember]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');

    try {
      let imageUrl = formData.imageUrl;
      
      // If there's a new image file, convert it to base64
      if (imageFile) {
        imageUrl = await convertImageToBase64(imageFile);
      }

      const teamMemberData = {
        ...formData,
        imageUrl
      };

      await onSave(teamMemberData);
      setSuccess('Team member saved successfully!');
      
      // Reset form if it's a new team member (no id)
      if (!teamMember?._id) {
        setFormData({
          name: '',
          position: '',
          imageUrl: '',
          bio: '',
          email: '',
          linkedin: ''
        });
        setImageFile(null);
        setImagePreview('');
      }
    } catch (err) {
      setError(err.message || 'Failed to save team member');
    } finally {
      setLoading(false);
    }
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  return (
    <ContentCard>
      <CardTitle>{teamMember?._id ? 'Edit Team Member' : 'Add New Team Member'}</CardTitle>
      
      {success && <SuccessMessage>{success}</SuccessMessage>}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <label htmlFor="name">Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter team member name"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="position">Position *</label>
          <input
            type="text"
            id="position"
            name="position"
            value={formData.position}
            onChange={handleInputChange}
            placeholder="Enter position/role"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="bio">Bio</label>
          <textarea
            id="bio"
            name="bio"
            value={formData.bio}
            onChange={handleInputChange}
            placeholder="Enter team member bio"
            rows={4}
          />
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Enter email address"
          />
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="linkedin">LinkedIn URL</label>
          <input
            type="url"
            id="linkedin"
            name="linkedin"
            value={formData.linkedin}
            onChange={handleInputChange}
            placeholder="Enter LinkedIn profile URL"
          />
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="image">Profile Image</label>
          <ImageUpload>
            <input
              type="file"
              id="image"
              accept="image/*"
              onChange={handleImageChange}
            />
            <span>Choose file...</span>
          </ImageUpload>
          
          {imagePreview && (
            <ImagePreview>
              <img src={imagePreview} alt="Preview" />
            </ImagePreview>
          )}
        </FormGroup>
        
        <ActionButtons>
          <CancelButton type="button" onClick={onCancel}>
            Cancel
          </CancelButton>
          <SaveButton type="submit" disabled={loading}>
            {loading ? (
              <>
                <AiOutlineLoading3Quarters className="loading-icon" />
                Saving...
              </>
            ) : (
              'Save Team Member'
            )}
          </SaveButton>
        </ActionButtons>
      </form>
    </ContentCard>
  );
};

export default TeamMemberEditor;
